<script setup lang="ts">
import { Button } from "@/components/ui/button";
import { useClipboard } from "@vueuse/core";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";

import CodeBlock from "@/components/CodeBlock.vue";

const props = defineProps<{
    token: string;
}>();

// using 2 different clipboard instances to avaid duplicated copy label
const clipboard1 = useClipboard();
const clipboard2 = useClipboard();

const code = `
<body>
    <div id="zaptime-container"></div>

    <${"script"} src="https://iframe.zaptime.app/zaptime.js"></${"script"}>

    <${"script"}>
        Zaptime({config: {token: '${props.token}' }})
            .render('#zaptime-container')
    </${"script"}>
</body>
`;

function copyCode() {
    clipboard1.copy(code);
}

function copyToken() {
    clipboard2.copy(props.token);
}
</script>

<template>
    <Dialog>
        <DialogTrigger as-child>
            <slot />
        </DialogTrigger>
        <DialogContent class="sm:max-w-[760px]">
            <DialogHeader>
                <DialogTitle>{{ $t("integration_code.title") }}</DialogTitle>

                <DialogDescription>
                    {{ $t("integration_code.description") }}
                    <a
                        class="font-medium text-iron-900 hover:underline"
                        href="https://docs.zaptime.app/guide/getting-started.html"
                        target="_blank"
                        >{{ $t("integration_code.documentation") }}.</a
                    >
                </DialogDescription>
            </DialogHeader>
            <div class="max-w-[350px] sm:max-w-[600px] md:max-w-[740px]">
                <CodeBlock :code="code"></CodeBlock>
            </div>
            <DialogFooter>
                <div class="flex w-full justify-between">
                    <DialogClose as-child>
                        <Button type="button" variant="secondary">
                            {{ $t("close") }}
                        </Button>
                    </DialogClose>

                    <div v-if="clipboard1.isSupported" class="flex gap-2">
                        <Button
                            type="button"
                            variant="secondary"
                            @click="copyCode"
                        >
                            {{
                                clipboard1.copied.value
                                    ? $t("copied")
                                    : $t("integration_code.copy_code")
                            }}
                        </Button>
                        <Button type="button" @click="copyToken">
                            {{
                                clipboard2.copied.value
                                    ? $t("copied")
                                    : $t("integration_code.copy_token")
                            }}
                        </Button>
                    </div>
                    <div v-else class="text-sm">
                        Missing permissions to copy code
                    </div>
                </div>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
